import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, Button, Badge } from '../components/ui';
import { api } from '../lib/api';
import { displayDate } from '../utils/dates';

interface TransactionResult {
  id: string;
  transaction_type: string;
  transaction_date?: string;
  sale_price?: number;
  price_per_sf?: number;
  cap_rate?: number;
  tenant_name?: string;
  rent_per_sf_year?: number;
  lease_term_months?: number;
  buyer_name?: string;
  seller_name?: string;
  days_on_market?: number;
  source?: string;
  property?: {
    id: string;
    address: string;
    city?: string;
    state?: string;
    property_type?: string;
    building_size?: number;
    year_built?: number;
    submarket?: string;
  };
}

const formatCurrency = (n?: number) => n ? `$${n.toLocaleString()}` : '—';
const formatPSF = (n?: number) => n ? `$${n.toFixed(2)}` : '—';
const formatPct = (n?: number) => n ? `${n.toFixed(2)}%` : '—';
const formatDate = (d?: string) => displayDate(d) ?? '—';
const formatSF = (n?: number) => n ? `${n.toLocaleString()} SF` : '—';

export default function CompSearch() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const subjectId = searchParams.get('subjectId');
  const [subjectAddress, setSubjectAddress] = useState('');
  const [results, setResults] = useState<TransactionResult[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState('');
  const [addingComp, setAddingComp] = useState<string | null>(null);
  const [addedComps, setAddedComps] = useState<Set<string>>(new Set());

  // Fetch subject property address when in add-comp mode
  useEffect(() => {
    if (!subjectId) return;
    api.getMasterProperty(subjectId)
      .then((data: any) => setSubjectAddress(data.property?.address || 'Unknown Property'))
      .catch(() => setSubjectAddress('Unknown Property'));
  }, [subjectId]);

  const handleAddAsComp = async (tx: TransactionResult) => {
    if (!subjectId) return;
    setAddingComp(tx.id);
    try {
      await api.addPooledComp(subjectId, {
        comp_property_id: tx.property?.id,
        address: tx.property?.address,
        city: tx.property?.city,
        state: tx.property?.state,
        sale_price: tx.sale_price,
        price_per_sf: tx.price_per_sf,
        cap_rate: tx.cap_rate,
        sale_date: tx.transaction_date,
        building_size: tx.property?.building_size,
        source: tx.source || 'comp_search',
      });
      setAddedComps(prev => new Set(prev).add(tx.id));
    } catch (err: any) {
      setError(err.message || 'Failed to add comp');
    } finally {
      setAddingComp(null);
    }
  };

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    city: '',
    submarket: '',
    property_type: '',
    property_subtype: '',
    transaction_type: '',
    min_price: '',
    max_price: '',
    min_size: '',
    max_size: '',
    min_date: '',
    // B2: closed sales only by default. An asking price is not a comp - it runs ~0.375pp
    // cheap against real closes, which flatters a deal. Kept as a visible toggle rather
    // than a hard exclusion, because a live listing at a comparable building is still real
    // market intelligence when you know that is what you are looking at.
    include_listings: '',
  });

  const handleSearch = useCallback(async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError('');
    setSearched(true);
    try {
      const params: Record<string, string> = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params[k] = v;
      });
      params.limit = '50';
      const data: any = await api.searchTransactions(params);
      setResults(data.transactions || []);
      setTotal(data.total || 0);
    } catch (err: any) {
      setError(err.message || 'Search failed');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const clearFilters = () => {
    setFilters({ search: '', city: '', submarket: '', property_type: '', property_subtype: '', transaction_type: '', min_price: '', max_price: '', min_size: '', max_size: '', min_date: '', include_listings: '' });
    setResults([]);
    setSearched(false);
  };

  const updateFilter = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="font-display text-[28px] font-semibold uppercase leading-[1.06] tracking-[-.01em] text-gray-900 sm:text-[32px]">Comp Search</h1>
          <p className="mt-1 text-sm text-gray-500">Search the entire transaction pool — every sale, lease, and listing in your proprietary database</p>
        </div>

        {/* Add-comp mode banner */}
        {subjectId && subjectAddress && (
          <div className="bg-info-wash border border-info/30 text-info px-4 py-3 rounded-lg text-sm flex items-center justify-between">
            <span>Adding comps for: <strong>{subjectAddress}</strong></span>
            <Link to={`/data-hub/${subjectId}`} className="text-info hover:underline text-xs">Back to property</Link>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
        )}

        {/* Search Filters */}
        <Card>
          <CardContent className="py-4">
            <form onSubmit={handleSearch}>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Search</label>
                  <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Address, city, tenant, buyer, seller..." value={filters.search} onChange={e => updateFilter('search', e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">City</label>
                  <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="e.g., Ontario" value={filters.city} onChange={e => updateFilter('city', e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Submarket</label>
                  <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="e.g., Inland Empire" value={filters.submarket} onChange={e => updateFilter('submarket', e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Property Type</label>
                  <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" value={filters.property_type} onChange={e => updateFilter('property_type', e.target.value)}>
                    <option value="">All Types</option>
                    <option value="industrial">Industrial</option>
                    <option value="retail">Retail</option>
                    <option value="office">Office</option>
                    <option value="multifamily">Multifamily</option>
                    <option value="land">Land</option>
                  </select>
                </div>
                <div>
                  {/* Subtype is the filter that makes "show me automotive buildings" mean it.
                      Counts are cap-rate-bearing sales, so the label says what the choice buys. */}
                  <label className="block text-xs font-medium text-gray-500 mb-1">Subtype</label>
                  <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" value={filters.property_subtype} onChange={e => updateFilter('property_subtype', e.target.value)}>
                    <option value="">Any subtype</option>
                    <optgroup label="Retail">
                      <option value="standalone_retail">Standalone retail (531)</option>
                      <option value="restaurant">Restaurant (113)</option>
                      <option value="auto_service">Auto service (98)</option>
                      <option value="strip_center">Strip center</option>
                      <option value="shopping_mall">Shopping mall</option>
                    </optgroup>
                    <optgroup label="Industrial">
                      <option value="warehouse">Warehouse (250)</option>
                      <option value="manufacturing">Manufacturing (47)</option>
                      <option value="distribution">Distribution (28)</option>
                      <option value="flex">Flex</option>
                      <option value="cold_storage">Cold storage</option>
                      <option value="truck_terminal">Truck terminal</option>
                    </optgroup>
                    <optgroup label="Office / other">
                      <option value="medical_office">Medical office</option>
                      <option value="creative_office">Creative office</option>
                      <option value="self_storage">Self storage</option>
                      <option value="hospitality">Hospitality</option>
                      <option value="school">School</option>
                      <option value="other">Other</option>
                    </optgroup>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Transaction Type</label>
                  <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" value={filters.transaction_type} onChange={e => updateFilter('transaction_type', e.target.value)}>
                    <option value="">All</option>
                    <option value="sale">Sale</option>
                    <option value="lease">Lease</option>
                    <option value="listing">Listing</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Min Price</label>
                  <input type="number" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="$0" value={filters.min_price} onChange={e => updateFilter('min_price', e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Max Price</label>
                  <input type="number" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="No max" value={filters.max_price} onChange={e => updateFilter('max_price', e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Min Size (SF)</label>
                  <input type="number" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="0" value={filters.min_size} onChange={e => updateFilter('min_size', e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Max Size (SF)</label>
                  <input type="number" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="No max" value={filters.max_size} onChange={e => updateFilter('max_size', e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">After Date</label>
                  <input type="date" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" value={filters.min_date} onChange={e => updateFilter('min_date', e.target.value)} />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300"
                      checked={filters.include_listings === 'true'}
                      onChange={e => updateFilter('include_listings', e.target.checked ? 'true' : '')}
                    />
                    Include active listings
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-4">
                <Button type="button" onClick={clearFilters} className="bg-gray-100 text-gray-700 hover:bg-gray-200">Clear</Button>
                <Button type="submit" disabled={loading}>{loading ? 'Searching...' : 'Search Transaction Pool'}</Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Results */}
        {searched && (
          <div>
            <p className="text-sm text-gray-500 mb-4">
              {loading ? 'Searching...' : `${total.toLocaleString()} transaction${total !== 1 ? 's' : ''} found`}
            </p>

            {!loading && results.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">No transactions match your search criteria</p>
                <p className="text-gray-400 text-sm mt-2">Try broadening your filters or importing more data</p>
              </div>
            ) : (
              <div className="space-y-3">
                {results.map(tx => (
                  <Card key={tx.id} className="hover:border-gray-900 transition-colors">
                    <CardContent className="py-4">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => tx.property?.id && navigate(`/data-hub/${tx.property.id}`)}>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={tx.transaction_type === 'sale' ? 'bg-info-wash text-info' : tx.transaction_type === 'lease' ? 'bg-gray-100 text-gray-800' : 'bg-gray-100 text-gray-700'}>
                              {tx.transaction_type}
                            </Badge>
                            {tx.property?.property_type && (
                              <Badge className="bg-gray-100 text-gray-600">{tx.property.property_type.replace(/_/g, ' ')}</Badge>
                            )}
                            {tx.source && (
                              <span className="text-xs text-gray-400">{tx.source}</span>
                            )}
                          </div>
                          <h3 className="text-base font-semibold text-gray-900">
                            {tx.property?.address || 'Unknown Address'}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {[tx.property?.city, tx.property?.state].filter(Boolean).join(', ')}
                            {tx.property?.submarket && <span className="ml-2 text-gray-400">({tx.property.submarket})</span>}
                          </p>
                          {tx.transaction_type === 'lease' && tx.tenant_name && (
                            <p className="text-sm text-gray-600 mt-1">Tenant: {tx.tenant_name}</p>
                          )}
                          {tx.transaction_type === 'sale' && (tx.buyer_name || tx.seller_name) && (
                            <p className="text-sm text-gray-600 mt-1">
                              {tx.buyer_name && <span>Buyer: {tx.buyer_name}</span>}
                              {tx.buyer_name && tx.seller_name && <span> | </span>}
                              {tx.seller_name && <span>Seller: {tx.seller_name}</span>}
                            </p>
                          )}
                        </div>

                        {/* Metrics */}
                        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm flex-shrink-0">
                          {tx.transaction_type === 'sale' ? (
                            <>
                              <div>
                                <p className="text-xs text-gray-400">Sale Price</p>
                                <p className="font-semibold text-gray-900">{formatCurrency(tx.sale_price)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-400">Price/SF</p>
                                <p className="font-semibold text-gray-900">{formatPSF(tx.price_per_sf)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-400">Cap Rate</p>
                                <p className="font-semibold text-gray-900">{formatPct(tx.cap_rate)}</p>
                              </div>
                            </>
                          ) : tx.transaction_type === 'lease' ? (
                            <>
                              <div>
                                <p className="text-xs text-gray-400">Rent/SF/Yr</p>
                                <p className="font-semibold text-gray-900">{formatPSF(tx.rent_per_sf_year)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-400">Term</p>
                                <p className="font-semibold text-gray-900">{tx.lease_term_months ? `${tx.lease_term_months}mo` : '—'}</p>
                              </div>
                            </>
                          ) : null}
                          <div>
                            <p className="text-xs text-gray-400">Size</p>
                            <p className="font-semibold text-gray-900">{formatSF(tx.property?.building_size)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-400">Date</p>
                            <p className="font-semibold text-gray-900">{formatDate(tx.transaction_date)}</p>
                          </div>
                        </div>

                        {/* Action buttons */}
                        <div className="flex flex-col gap-1 flex-shrink-0">
                          {subjectId ? (
                            addedComps.has(tx.id) ? (
                              <span className="text-xs text-success font-medium px-3 py-1.5">Added</span>
                            ) : (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleAddAsComp(tx); }}
                                disabled={addingComp === tx.id}
                                className="text-xs px-3 py-1.5 bg-info-wash text-info rounded-lg hover:bg-info-wash transition-colors disabled:opacity-50"
                              >
                                {addingComp === tx.id ? 'Adding...' : 'Add as Comp'}
                              </button>
                            )
                          ) : tx.property?.id ? (
                            <Link
                              to={`/data-hub/${tx.property.id}`}
                              onClick={(e) => e.stopPropagation()}
                              className="text-xs px-3 py-1.5 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-center"
                            >
                              View in Data Hub
                            </Link>
                          ) : null}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Empty state before search */}
        {!searched && (
          <div className="text-center py-16">
            <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-700">Search your proprietary comp database</h3>
            <p className="text-gray-400 text-sm mt-2 max-w-md mx-auto">
              Find sales, leases, and listings across every property in your pool.
              Filter by location, price, size, date, and type.
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}
